import {useSyncExternalStore} from 'react';
import fr from '../data/fr.json';
import en from '../data/en.json';
import routes from '../data/routes.json';

/**
 * Les deux langues du site, sans bibliothèque.
 *
 * ## Ce qui a été retiré, et pourquoi
 *
 * `i18next` faisait ce travail. Mesuré sur son bundle ESM : 106 kio minifiés, **21 kio après
 * gzip**, dans le bundle principal — donc bloquant le premier rendu, sur un total de 96 kio.
 * Un cinquième du poids servi avant la première image, pour ce que le site emploie réellement :
 * une recherche à plat dans 116 clés et quatre substitutions `{{...}}`. Aucune clé au pluriel,
 * aucun `<Trans>`, aucun détecteur, aucun backend, et aucun appel à son API en dehors de ce
 * dossier — la surface publique tenait déjà dans les six fonctions ci-dessous.
 *
 * C'est le même raisonnement que le bundle du widget, qui porte ses libellés dans un objet
 * plutôt qu'une bibliothèque parce qu'il est servi chez un tiers.
 *
 * L'API exportée est **inchangée** : aucun appelant n'a bougé.
 *
 * ## Les ressources restent embarquées
 *
 * Les deux catalogues sont importés, donc résolus au chargement : aucune requête réseau, et
 * aucun éclair de français sur `/en`. Ils pèsent 6 kio gzip à eux quatre, ce qui est le prix
 * réel du multilingue ici.
 */
export type SiteLanguage = 'fr' | 'en';

/**
 * Les deux catalogues, et la garantie qu'ils se correspondent.
 *
 * Typer `en` par la forme de `fr` refuse à la compilation un catalogue anglais auquel il manque
 * une clé — en la nommant. Une traduction oubliée ne peut donc pas partir en production.
 *
 * ## Un seul fichier par langue
 *
 * Il y en avait deux : `messages/` pour les libellés dits génériques, `content/` pour les textes
 * dits propres au client. La frontière ne tenait pas — treize des vingt-quatre clés de `content`
 * étaient génériques, et `messages` portait déjà `menu.bodeguita`, qui nomme le coin tapas de ce
 * restaurant. Elle imposait en revanche de savoir dans quel fichier vivait une clé avant de
 * pouvoir la lire, et deux conventions de nommage pour une même chose.
 *
 * Ce que la séparation cherchait à dire — *quelles chaînes réécrire pour un autre client* — est
 * désormais **calculé** par `scripts/check-client.mjs`, qui relève les valeurs portant les noms
 * propres du client courant. Juste par construction, là où le classement manuel se trompait.
 */
const CATALOGUES: Record<SiteLanguage, typeof fr> = {fr, en};

/** Les clés dont la valeur est du texte — `t` ne rend que celles-là. */
type CleTexte = {[K in keyof typeof fr]: (typeof fr)[K] extends string ? K : never}[keyof typeof fr];

/**
 * La langue vient du chemin, jamais d'une préférence stockée : l'URL est la source de vérité.
 *
 * La même expression vit dans `public/index.html`, en ligne, pour poser `lang` **avant** ce
 * bundle — le loader du widget la lit à ce moment-là. Deux copies, parce qu'aucune ne peut
 * attendre l'autre : les modifier ensemble.
 */
export const languageFromPath = (path: string): SiteLanguage => /^\/en(?:\/|$)/.test(path) ? 'en' : 'fr';

let current: SiteLanguage = languageFromPath(window.location.pathname);
export const language = (): SiteLanguage => current;

/**
 * Abonnés au changement de langue.
 *
 * `useSyncExternalStore` plutôt qu'un contexte : la langue vit hors de React — le widget la lit
 * dans le DOM, `App` l'applique dans un effet de mise en page — et un `Set` de rappels rend
 * l'abonnement d'un composant indépendant de sa place dans l'arbre.
 */
const listeners = new Set<() => void>();
const subscribe = (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
};

export function useSiteLanguage(): SiteLanguage {
    return useSyncExternalStore(subscribe, language, language);
}

/**
 * Le libellé d'une clé, dans la langue courante.
 *
 * Trois replis en cascade, du plus précis au plus lisible : la langue courante, le français,
 * puis la clé elle-même. Rendre la clé plutôt qu'une chaîne vide est délibéré — un libellé
 * manquant doit se voir en relecture, pas disparaître de l'écran.
 */
export const t = (key: CleTexte, values?: Record<string, unknown>): string => {
    const template = CATALOGUES[current][key] ?? CATALOGUES.fr[key] ?? key;
    if (values === undefined) return template;
    return template.replace(/\{\{(\w+)\}\}/g, (_, name: string) => String(values[name] ?? ''));
};

/** Preserve the page, query and anchor when switching language. */
export function localizePath(path: string, locale: SiteLanguage = language()): string {
    const url = new URL(path, window.location.origin);
    if (url.origin !== window.location.origin) return path;
    const route = Object.values(routes).find(route => Object.values(route).includes(url.pathname));
    return (route?.[locale] ?? (locale === 'en' ? '/en' : '/')) + url.search + url.hash;
}

export function setSiteLanguage(locale: SiteLanguage) {
    if (current !== locale) {
        current = locale;
        listeners.forEach(listener => {
            listener();
        });
    }
    document.documentElement.lang = locale;
    document.querySelectorAll<HTMLScriptElement>('script[data-restaurant]').forEach(script => {
        script.dataset.locale = locale;
    });
    document.dispatchEvent(new CustomEvent('restaurant-booking:locale', {detail: {locale}}));
}
